<template>
    <label class="stakeBox" v-bind:class="{ _invalid: isInvalid, _disabled: disabled }">
        <span class="notranslate stakeBox-currency">{{ currency }}</span>
        <input
            ref="inputElement"
            v-bind:class="`${gaClassPrefix}_TextField_Stake`"
            v-bind:readonly="true"
            v-bind:disabled="disabled"
            v-bind:value="stakeValue"
            v-bind:placeholder="placeholder"
            v-bind:aria-invalid="isInvalid"
            v-on:mousedown.prevent
            v-on:click="emit('activate')"
        >
        <button
            v-show="hasStake"
            class="stakeBox-clear"
            v-bind:class="`${gaClassPrefix}_Btn_ClearStake`"
            type="button"
            aria-label="Clear"
            data-testid="stake-box-clear"
            v-bind:disabled="disabled"
            v-on:mousedown.prevent
            v-on:click="clearStake"
        >
            <SboIcon name="system:cross" size="16" />
        </button>
    </label>
</template>

<script setup lang="ts">
    import { ref, toRefs, watch } from 'vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import { EMPTY_STAKE, useStakeInput } from '@/libraries/composables/useStakeInput';

    const props = withDefaults(defineProps<{
        stake: number;
        minBet: number;
        maxBet: number;
        isInvalid: boolean;
        isActive?: boolean;
        disabled?: boolean;
        gaClassPrefix?: string;
    }>(), {
        isActive: false,
        disabled: false,
        gaClassPrefix: '',
    });

    const emit = defineEmits<{
        activate: [];
        updateStake: [stake: number];
    }>();

    const {
        stake,
        minBet,
        maxBet,
        isActive,
    } = toRefs(props);

    const {
        currency,
        hasStake,
        stakeValue,
        placeholder,
    } = useStakeInput(stake, { minBet, maxBet });

    const inputElement = ref<HTMLInputElement | null>(null);

    watch(isActive, (value) => {
        if (!value) {
            inputElement.value?.blur();
            return;
        }
        focusInputAtEnd();
    });

    function focusInputAtEnd() {
        const input = inputElement.value;
        if (!input) {
            return;
        }
        input.focus({ preventScroll: true });
        input.setSelectionRange(input.value.length, input.value.length);
    }

    function clearStake() {
        emit('updateStake', EMPTY_STAKE);
        emit('activate');
    }
</script>

<style lang="scss" scoped>
@import "@/scss/components/singleMode/stakeBox";
</style>
